import { useState } from 'react'
import {
  Activity,
  BadgeCheck,
  CalendarClock,
  DollarSign,
  Factory,
  FileText,
  Fingerprint,
  FlaskConical,
  MousePointerClick,
  Paperclip,
  Ruler,
  Scale,
  Thermometer,
  Warehouse,
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ActionsConfig } from './ActionsConfig'
import { ProfilesConfig } from './ProfilesConfig'
import { StorageZonesConfig } from './StorageZonesConfig'
import { TransferReasonCatalog } from './storage/TransferReasonCatalog'
import { PROFILE_TYPE_COLOR } from '@/lib/inventoryProfiles'
import { cn } from '@/lib/utils'
import type { InventoryProfileType } from '@/types/inventory'

const PROFILE_TYPES: { value: InventoryProfileType; label: string; icon: React.ElementType }[] = [
  { value: 'material_description', label: 'Material Description', icon: FileText },
  { value: 'manufacturer', label: 'Manufacturer', icon: Factory },
  { value: 'inventory_unit', label: 'Inventory Unit', icon: Ruler },
  { value: 'storage', label: 'Storage Profile', icon: Thermometer },
  // Product Code is intentionally absent: it is no longer a profile category.
  // A product code belongs to one material, not to a shared named profile, so
  // it is captured on the material itself when it is created.
  { value: 'traceability', label: 'Traceability', icon: Fingerprint },
  { value: 'shelf_life', label: 'Shelf Life Days', icon: CalendarClock },
  { value: 'quality', label: 'Quality Status', icon: BadgeCheck },
  { value: 'usage', label: 'Usage', icon: FlaskConical },
  { value: 'regulatory', label: 'Regulatory', icon: Scale },
  { value: 'cost', label: 'Cost', icon: DollarSign },
  { value: 'attachments', label: 'Attachments', icon: Paperclip },
  { value: 'actions', label: 'Actions', icon: MousePointerClick },
  { value: 'status', label: 'Status', icon: Activity },
]

/** Master Data Config: an icon sub-tab per category, each managing its named profiles + fields. */
const ZONES_TAB = 'storage_zones'

// Shared by every chip. Colour identifies the CATEGORY, so selection cannot also
// be a colour: the active chip keeps the solid primary fill it already had, which
// overrides the tint and stays the one unambiguous "you are here" signal.
const CHIP_BASE =
  'gap-1.5 rounded-full border px-3.5 py-2 shadow-none transition-colors ' +
  'data-[state=active]:border-primary data-[state=active]:bg-primary ' +
  'data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm'

const NEUTRAL_CHIP = 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'

export function MasterConfigTab() {
  const [type, setType] = useState<InventoryProfileType | typeof ZONES_TAB>('material_description')
  return (
    <Tabs value={type} onValueChange={(v) => setType(v as InventoryProfileType | typeof ZONES_TAB)}>
      <TabsList className='mb-5 flex h-auto flex-wrap justify-start gap-2 bg-transparent px-1 py-1'>
        {PROFILE_TYPES.map(({ value, label, icon: Icon }) => (
          <TabsTrigger
            key={value}
            value={value}
            className={cn(CHIP_BASE, PROFILE_TYPE_COLOR[value].chip)}
          >
            <Icon className='h-4 w-4' />
            {label}
          </TabsTrigger>
        ))}
        <TabsTrigger
          value={ZONES_TAB}
          // Storage Zones is not an InventoryProfileType, so it has no category
          // colour and deliberately stays neutral — it manages places, not a
          // profile category, and tinting it would imply it is one of the 13.
          className={cn(CHIP_BASE, NEUTRAL_CHIP)}
        >
          <Warehouse className='h-4 w-4' />
          Storage Zones
        </TabsTrigger>
      </TabsList>
      {PROFILE_TYPES.map(({ value }) => (
        <TabsContent key={value} value={value} className='mt-6'>
          {/* Actions are built in and all start on, so they get a list of
              switches rather than a table of profiles to build. */}
          {value === 'actions' ? <ActionsConfig /> : <ProfilesConfig profileType={value} />}
        </TabsContent>
      ))}
      <TabsContent value={ZONES_TAB} className='mt-6 space-y-8'>
        <StorageZonesConfig />
        {/* Under Storage Zones rather than a tab of its own: both are small
            lists that describe how this site's storage is organised, and a tab
            holding one table would read as a bigger surface than it is. */}
        <TransferReasonCatalog />
      </TabsContent>
    </Tabs>
  )
}
